import { useMemo, useState } from 'react';
import { Box, Button, FormLabel, MenuItem, Select, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GlobalDialogContent from '../GlobalDialogContent';
import { RootState } from '../../../redux/store';
import { closeDialog } from '../../../redux/reducer/dialogSlice';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import { setModuleRefresh } from '../../../redux/reducer/registration/RefreshSlice';
import { UpdateProjectBeneficiaryStatusService } from '../../../utils/services/collaboration-agreement/collaboration-agreement.service';
import { BeneficiaryStatus, UserRole } from '../../../utils/collaboration-agreement-actions';

const titleCase = (status: string) => status.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

interface StatusOption {
    value: string;
    label: string;
}

const toOption = (value: string, label?: string): StatusOption => ({ value, label: label ?? titleCase(value) });

const VERIFIER_ROLES: ReadonlyArray<string> = [
    UserRole.CMRF_DEPARTMENT,
    UserRole.GOVERNMENT_USER,
    UserRole.CSR_CELL,
];

const isVerifierRole = (userType: string) => VERIFIER_ROLES.includes(userType);

const getStatusOptions = (currentStatus: string, userType: string, allDocumentsApproved: boolean): StatusOption[] => {
    if (isVerifierRole(userType) && currentStatus === BeneficiaryStatus.DISBURSEMENT_DONE_BY_CMRF) {
        return [toOption(BeneficiaryStatus.NEED_MORE_HELP), toOption(BeneficiaryStatus.COMPLETE)];
    }
    if (userType === UserRole.HOSPITAL) {
        return [toOption(BeneficiaryStatus.PATIENT_BACKOUT), toOption(BeneficiaryStatus.PATIENT_GONE_EARLY)];
    }
    if (currentStatus === BeneficiaryStatus.PRE_AUTH_PENDING) {
        const options: StatusOption[] = [];
        if (allDocumentsApproved) {
            options.push(toOption(BeneficiaryStatus.PRE_AUTH_DONE));
        }
        options.push(toOption(BeneficiaryStatus.NEED_ATTENTION, 'Send back to hospital'));
        return options;
    }
    if (isVerifierRole(userType) && currentStatus === BeneficiaryStatus.POST_AUTH_DONE) {
        return [toOption(BeneficiaryStatus.POST_AUTH_NEED_ATTENTION, 'Send back to hospital')];
    }
    if (currentStatus === BeneficiaryStatus.POST_AUTH_PENDING) {
        if (isVerifierRole(userType)) {
            if (!allDocumentsApproved) {
                return [toOption(BeneficiaryStatus.POST_AUTH_NEED_ATTENTION, 'Send back to hospital')];
            }
            return [
                toOption(BeneficiaryStatus.POST_AUTH_DONE),
                toOption(BeneficiaryStatus.POST_AUTH_NEED_ATTENTION, 'Send back to hospital'),
            ];
        }
        return [
            toOption(BeneficiaryStatus.POST_AUTH_DONE),
            toOption(BeneficiaryStatus.POST_AUTH_NEED_ATTENTION, 'Send back to hospital'),
        ];
    }
    return [];
};

const BeneficiaryStatusDialog = () => {
    const dispatch = useDispatch();
    const { payload } = useSelector((state: RootState) => state.dialog);
    const authuser = useSelector((state: RootState) => state.authUser.userDetails);
    const userType = authuser?.user_type || '';
    const currentStatus = payload?.status || BeneficiaryStatus.PRE_AUTH_PENDING;
    const allDocumentsApproved = payload?.allDocumentsApproved === true;

    const beneficiaryStatusOptions = useMemo(
        () => getStatusOptions(currentStatus, userType, allDocumentsApproved),
        [currentStatus, userType, allDocumentsApproved]
    );

    const [status, setStatus] = useState<string>(beneficiaryStatusOptions[0]?.value || '');
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!status) {
            dispatch(showSnackbar({ type: 'error', message: 'Please select a status' }));
            return;
        }
        if (!remarks.trim()) {
            dispatch(showSnackbar({ type: 'error', message: 'Please enter remark' }));
            return;
        }

        setLoading(true);
        const { code, message } = await UpdateProjectBeneficiaryStatusService({
            uuid: payload?.uuid,
            status,
            remarks,
        });

        if (code === 200) {
            dispatch(showSnackbar({ type: 'success', message: message || 'Beneficiary status updated successfully' }));
            dispatch(setModuleRefresh({ moduleName: 'user_details', refresh: true }));
            dispatch(closeDialog());
        } else {
            dispatch(showSnackbar({ type: 'error', message: message || 'Something went wrong' }));
        }
        setLoading(false);
    };

    return (
        <GlobalDialogContent
            dialogBody={
                <>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>Status <span style={{ color: 'red' }}>*</span></FormLabel>
                        <Select fullWidth size="small" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ mt: 0.5 }}>
                            {beneficiaryStatusOptions.map((item) => (
                                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box>
                        <FormLabel>Remark <span style={{ color: 'red' }}>*</span></FormLabel>
                        <TextField fullWidth size="small" multiline minRows={3} value={remarks} onChange={(event) => setRemarks(event.target.value)} placeholder="Enter remark" sx={{ mt: 0.5 }} />
                    </Box>
                </>
            }
            dialogFooter={
                <Button variant="contained" color="success" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Updating...' : 'Update'}
                </Button>
            }
        />
    );
};

export default BeneficiaryStatusDialog;
